import {
  StrategyDetailsPageCommitmentsByInvestorQuery,
  StrategyDetailsPageInvestmentEntitiesByAdvisoryFirmQuery,
  StrategyDetailsPageInvestmentEntitiesQuery
} from '@/__generated__/graphql';
import client from '@/apollo-client';
import { ISelectValue } from '@/components/basicComponents/select';
import { ALL_ADVISORY_FIRM_LIST_QUERY, GET_INVESTOR_LEGAL_ENTITIES_IDS_QUERY } from '@/components/investors-page/queries';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { SORT_ORDER } from '@/components/table/types';
import { IMobileSort } from '@/components/transactions-page/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { advisoryFirmTableSort, entityTableSort, investorTableSort } from './constants';
import InvestorsTabTable from './investorsTabTable/table';
import {
  STRATEGY_DETAILS_PAGE_COMMITMENTS_BY_INVESTOR_QUERY,
  STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_BY_ADVISORY_FIRM_QUERY,
  STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_QUERY
} from './queries';

export const InvestorsTab = () => {
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const {
    searchParams,
    setSearchParams,
    onChangeSearch,
    onChangeSort,
    onChangePrimaryFilter,
    onChangePaginationData,
    saveScrollPostion,
    makeScroll
  } = useSearchParamsManager();

  const strategyId = params?.strategyId || '';

  const q = searchParams.get('q') || '';
  const viewBy = searchParams.get('viewBy') || '';
  const key = searchParams.get('key') || 'name';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const primaryFilter = searchParams.get('primaryFilter') || '';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const position = searchParams.get('position') || 0;

  const [entitiesTableRows, setEntitiesTableRows] = useState<StrategyDetailsPageInvestmentEntitiesQuery['InvestmentEntities']['entities']>([]);
  const [advisoryFirmTableRows, setAdvisoryFirmTableRows] = useState<
    StrategyDetailsPageInvestmentEntitiesByAdvisoryFirmQuery['InvestmentEntitiesByAdvisoryFirm']['firms']
  >([]);
  const [investorTableRows, setInvestorTableRows] = useState<StrategyDetailsPageCommitmentsByInvestorQuery['CommitmentsByInvestor']['investors']>([]);
  const [primaryFilterList, setPrimaryFilterList] = useState<ISelectValue[]>([]);
  const [viewByList, setViewByList] = useState<ISelectValue[]>([]);
  const [mobileSortItems, setMobileSortItems] = useState<IMobileSort[]>([]);
  const [mobileSort, setMobileSort] = useState('');
  const sortItem = mobileSortItems.find((item) => item.title === mobileSort);
  const mobileSortList = mobileSortItems.map((item) => item.title);

  useEffect(() => {
    if (!user) return;

    if (user.tenant.type === TenantType.ADVISORY_FIRM) {
      setViewByList([
        { label: 'Investor', value: 'Investor' },
        { label: 'Entity', value: 'Entity' }
      ]);
      if (!viewBy) {
        onChangeViewBy('Investor');
      }
      return;
    }

    if (user.tenant.type === TenantType.PLATFORM_MANAGER) {
      setViewByList([
        { label: 'Advisory Firm', value: 'Advisory Firm' },
        { label: 'Investor', value: 'Investor' },
        { label: 'Entity', value: 'Entity' }
      ]);
      if (!viewBy) {
        onChangeViewBy('Advisory Firm');
      }
      return;
    }
  }, [user]);

  useEffect(() => {
    if (!isMobile) return;

    switch (viewBy) {
      case 'Advisory Firm':
        const filteredAdvisoryFirmTableSort = filteredMobileSortItems(advisoryFirmTableSort);
        setMobileSortItems(filteredAdvisoryFirmTableSort);
        setMobileSort(filteredAdvisoryFirmTableSort[0].title);
        break;
      case 'Investor':
        const filteredInvestorTableSort = filteredMobileSortItems(investorTableSort);
        setMobileSortItems(filteredInvestorTableSort);
        setMobileSort(filteredInvestorTableSort[0].title);
        break;
      case 'Entity':
        const filteredEntityTableSort = filteredMobileSortItems(entityTableSort);
        setMobileSortItems(filteredEntityTableSort);
        setMobileSort(filteredEntityTableSort[0].title);
        break;
      default:
        break;
    }
  }, [viewBy, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    getCurrentStatus().refetch({
      order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      sort: sortItem?.key
    });
  }, [mobileSort]);

  const { data: listAdvisors } = useQuery(ALL_ADVISORY_FIRM_LIST_QUERY, {
    skip: Boolean(user && user.tenant.type !== TenantType.PLATFORM_MANAGER),
    onCompleted: ({ allAdvisoryFirmList }) => {
      setPrimaryFilterList([...new Set(allAdvisoryFirmList.map((el) => ({ label: el.name, value: el.name })))]);
    }
  });

  const {
    data: entitiesData,
    loading: entitiesLoading,
    refetch: entitiesRefetch
  } = useQuery(STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !strategyId || viewBy !== 'Entity',
    variables: {
      id: strategyId,
      sort: key,
      order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      search: q.toLowerCase().trim(),
      rowsPerPage: Number(limit),
      page: Math.floor(Number(offset) / Number(limit)) + 1,
      quickFilter: primaryFilter ? primaryFilter : null
    },
    onCompleted: (data) => {
      setEntitiesTableRows(data.InvestmentEntities.entities);
      makeScroll(position);
    }
  });

  const {
    data: advisoryFirmsData,
    loading: advisoryFirmsLoading,
    refetch: advisoryFirmsRefetch
  } = useQuery(STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_BY_ADVISORY_FIRM_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !strategyId || viewBy !== 'Advisory Firm',
    variables: {
      id: strategyId,
      sort: key,
      order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      search: q.toLowerCase().trim(),
      rowsPerPage: Number(limit),
      page: Math.floor(Number(offset) / Number(limit)) + 1,
      quickFilter: primaryFilter ? primaryFilter : null
    },
    onCompleted: (data) => {
      setAdvisoryFirmTableRows(data.InvestmentEntitiesByAdvisoryFirm.firms);
      makeScroll(position);
    }
  });

  const {
    data: investorsData,
    loading: investorsLoading,
    refetch: investorsRefetch
  } = useQuery(STRATEGY_DETAILS_PAGE_COMMITMENTS_BY_INVESTOR_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !strategyId || viewBy !== 'Investor',
    variables: {
      investmentId: strategyId,
      sort: key,
      order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      search: q.toLowerCase().trim(),
      rowsPerPage: Number(limit),
      page: Math.floor(Number(offset) / Number(limit)) + 1,
      quickFilter: primaryFilter ? primaryFilter : null
    },
    onCompleted: (data) => {
      setInvestorTableRows(data.CommitmentsByInvestor.investors);
      makeScroll(position);
    }
  });

  const getCurrentStatus = () => {
    switch (viewBy) {
      case 'Advisory Firm':
        return {
          refetch: advisoryFirmsRefetch,
          total: advisoryFirmsData?.InvestmentEntitiesByAdvisoryFirm?.total ?? 0
        };
      case 'Investor':
        return {
          refetch: investorsRefetch,
          total: investorsData?.CommitmentsByInvestor?.total ?? 0
        };
      case 'Entity':
        return {
          refetch: entitiesRefetch,
          total: entitiesData?.InvestmentEntities?.total ?? 0
        };
      default:
        return {
          refetch: (variables?: any) => null as any,
          total: 0
        };
    }
  };

  const filteredMobileSortItems = (items: IMobileSort[]) => {
    if (user && user.tenant.type === TenantType.ADVISORY_FIRM) {
      return items.filter((item) => item.key !== 'ADVISORY_FIRMS');
    }
    return items;
  };

  const openInvestorDetailsPage = async ({ investorId, entityId }: { investorId: string; entityId?: string }) => {
    try {
      const { data } = await client.query({
        query: GET_INVESTOR_LEGAL_ENTITIES_IDS_QUERY,
        variables: {
          data: {
            id: investorId
          }
        }
      });

      const { legalEntities } = data.getInvestor;
      saveScrollPostion();

      if (!entityId && legalEntities?.length === 1) {
        const entity = legalEntities?.[0];
        navigate(`/investors/${investorId}/${entity?.id}?isGroup=${entity?.isGroup}&backToTitle=Strategy Details`);
        return;
      }

      if (!entityId) {
        navigate(`/investors/${investorId}?backToTitle=Strategy Details`);
        return;
      }

      if (entityId) {
        const entity = legalEntities?.find((entity) => entity.id === entityId);
        navigate(`/investors/${investorId}/${entity?.id}?isGroup=${entity?.isGroup}&backToTitle=Strategy Details`);
        return;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const openAdvisorFirmDetails = (id: string) => {
    onChangeViewBy('Investor');
    const selectedAdvisor = listAdvisors.allAdvisoryFirmList.find((item) => item.id === id)?.name || 'All';
    onChangePrimaryFilter(selectedAdvisor);
  };

  const onChangeViewBy = (viewByValue: string) => {
    setSearchParams(
      (prev) => {
        prev.set('viewBy', viewByValue);
        viewByValue === 'Advisory Firm' && prev.delete('primaryFilter');
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <>
      <FilterPage
        search={{
          value: q,
          onChange: onChangeSearch,
          placeholder: 'Search for an investor'
        }}
        resultsValue={getCurrentStatus().total}
        refetch={getCurrentStatus().refetch}
        useNewFilters
        viewByQuickFilter={{
          title: 'View By',
          value: viewBy,
          defaultValue: 'All',
          options: viewByList,
          onChange: onChangeViewBy
        }}
        mobileSort={{
          value: mobileSort,
          onChange: setMobileSort,
          selects: mobileSortList
        }}
        {...(user &&
          user.tenant.type === TenantType.PLATFORM_MANAGER &&
          viewBy !== 'Advisory Firm' && {
            advisoryFirmQuickFilter: {
              title: 'Advisory Firm',
              value: primaryFilter,
              defaultValue: 'All',
              options: [{ label: 'All', value: '' }, ...primaryFilterList],
              onChange: onChangePrimaryFilter
            }
          })}
      />
      <InvestorsTabTable
        advisoryFirmTableRows={advisoryFirmTableRows}
        entityTableRows={entitiesTableRows}
        investorTableRows={investorTableRows}
        refetch={getCurrentStatus().refetch}
        loading={entitiesLoading || advisoryFirmsLoading || investorsLoading}
        viewByValue={viewBy}
        sort={{ key, asc }}
        setSort={onChangeSort}
        openInvestorDetailsPage={openInvestorDetailsPage}
        openAdvisorFirmDetails={openAdvisorFirmDetails}
      />
      <PaginationWrap>
        <TablePagination
          savePagination={onChangePaginationData}
          paginationValues={{
            limit: Number(limit),
            offset: Number(offset),
            total: getCurrentStatus().total
          }}
          refetch={() => null as any}
        />
      </PaginationWrap>
    </>
  );
};

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
